import { AxiosResponse } from 'axios';
import { get } from 'lodash';
import { UserRole } from '../../mnspro-cloud/api/v2';
import { createGraphApi } from '../factory';
import { ValueOf, ValueOfPaginated } from '../helper/helper';
import { removeServiceUserFromCourseMembers } from './../../utils/serviceUserUtils';

export interface CourseMember {
  id: string;
  accountEnabled: boolean;
  displayName: string;
  givenName: string;
  surname: string;
  userPrincipalName: string;
  userType?: string;
  userRole?: string;
}

export interface Absence {
  comment: string;
  offset: string;
  reason: string;
  studentDisplayName: string;
  studentId: string;
  studentUserPrincipalName: string;
  timestamp: string;
  userId: string;
}

export interface AbsenceStudentEntry {
  /**
   *
   * @type {string}
   * @memberof AbsenceStudentEntry
   */
  studentId?: string | null;
  /**
   *
   * @type {(string | null)}
   * @memberof AbsenceStudentEntry
   */
  studentDisplayName?: string | null;
  /**
   *
   * @type {(string | null)}
   * @memberof AbsenceStudentEntry
   */
  studentUserPrincipalName?: string | null;
  /**
   *
   * @type {Array<AbsenceEntry>}
   * @memberof AbsenceStudentEntry
   */
  absences?: Array<AbsenceEntry> | null;
}

export interface AbsencesUpdateRequest {
  /**
   *
   * @type {string}
   * @memberof AbsencesUpdateRequest
   */
  organizationId?: string;
  /**
   *
   * @type {Array<AbsenceUpdateEntry>}
   * @memberof AbsencesUpdateRequest
   */
  absences?: Array<AbsenceUpdateEntry> | null;
}

export interface AbsenceUpdateEntry {
  /**
   *
   * @type {string}
   * @memberof AbsenceUpdateEntry
   */
  studentId?: string | null;
  /**
   *
   * @type {string}
   * @memberof AbsenceUpdateEntry
   */
  reason?: string | null;
  /**
   *
   * @type {string}
   * @memberof AbsenceUpdateEntry
   */
  timestamp?: string;
  /**
   *
   * @type {string}
   * @memberof AbsenceUpdateEntry
   */
  offset?: string;
  /**
   *
   * @type {string}
   * @memberof AbsenceUpdateEntry
   */
  comment?: string | null;
}

export interface AbsenceEntry {
  /**
   *
   * @type {string}
   * @memberof AbsenceEntry
   */
  reason?: string | null;
  /**
   *
   * @type {string}
   * @memberof AbsenceEntry
   */
  timestamp?: string;
  /**
   *
   * @type {string}
   * @memberof AbsenceEntry
   */
  offset?: string;
  /**
   *
   * @type {string}
   * @memberof AbsenceEntry
   */
  comment?: string | null;
  /**
   *
   * @type {string}
   * @memberof AbsenceEntry
   */
  userId?: string | null;
}

export interface Course {
  id: string;
  description: string;
  displayName: string;
  mailNickname: string;
  mail: string;
  members?: CourseMember[];
  period?: string;
}

export interface Assignment {
  id: string;
  classId: string;
  displayName: string;
  closeDateTime: string;
  dueDateTime: string;
  assignDateTime: string;
  assignedDateTime: string;
  allowLateSubmissions: boolean;
  createdDateTime: string;
  lastModifiedDateTime: string;
  allowStudentsToAddResourcesToSubmission: boolean;
  status?: string;
  fromTeams?: boolean;
  description?: string;
}
export interface ClassbookEntry {
  id: string;
  description?: string;
  status?: string;
}

export interface RoomService {
  id: string;
  description?: string;
  serviceOwner?: CourseMember;
  startDate?: string;
  endDate?: string;
}

export interface SchoolClass {
  id: string;
  description: string;
  displayName: string;
  Courses: Course[];
}

const COURSE_MEMBER_SELECT =
  'id,accountEnabled,displayName,givenName,surname,userPrincipalName,userType,aixconcept_MNSproUserV1';

/**
 * Largest page size Microsoft Graph accepts for group members/owners.
 * Without `$top` Graph returns only 100 entries per page.
 */
const GRAPH_GROUP_MEMBERS_PAGE_SIZE = 999;

class CourseService {
  static async courseGetById(
    authToken: string,
    courseId: string,
    withMembers = false,
  ): Promise<Course> {
    const response = await createGraphApi(authToken).get<Course>(
      `/groups/${courseId}`,
    );
    const course = { ...response.data };

    if (withMembers) {
      const members = await CourseService.getAllGroupDirectoryObjects(
        authToken,
        courseId,
        'members',
      );
      course.members = members.map(m => ({
        ...m,
        // @ts-ignore
        userRole: m['aixconcept_MNSproUserV1'].role as string,
      }));
    }

    return course;
  }

  static async courseGetAll(authToken: string): Promise<Course[]> {
    const response = await createGraphApi(authToken).get<ValueOf<Course[]>>(
      `/groups/`,
    );

    const result: Course[] = [];
    response.data.value.forEach(value => {
      result.push(value);
    });
    return result;
  }

  static async getCourseMembers(
    authToken: string,
    courseId: string,
  ): Promise<CourseMember[]> {
    const members = await CourseService.getCourseMembersByType(
      authToken,
      courseId,
      'members',
    );
    return removeServiceUserFromCourseMembers(members);
  }

  static async getCourseOwners(
    authToken: string,
    courseId: string,
  ): Promise<CourseMember[]> {
    const owners = await CourseService.getCourseMembersByType(
      authToken,
      courseId,
      'owners',
    );
    return removeServiceUserFromCourseMembers(owners);
  }

  static async getCourseMembersByType(
    authToken: string,
    courseId: string,
    type: 'members' | 'owners',
  ): Promise<CourseMember[]> {
    const records = await CourseService.getAllGroupDirectoryObjects(
      authToken,
      courseId,
      type,
    );

    return (
      records
        // Guest users added via azure do not have the aixconcept_MNSproUserV1 property
        .filter(m => get(m, 'aixconcept_MNSproUserV1', false) !== false)
        .map(m => ({
          ...m,
          userRole: get(m, 'aixconcept_MNSproUserV1', { role: UserRole.none })
            .role as string,
        }))
    );
  }

  /**
   * Loads all members or owners of a group from Microsoft Graph.
   * Graph pages the result, so every `@odata.nextLink` is followed
   * until the last page has been loaded.
   *
   * @param authToken Microsoft Graph bearer token
   * @param courseId Id of the group
   * @param type Whether to load the members or the owners of the group
   * @returns All members/owners of the group across all pages
   */
  private static async getAllGroupDirectoryObjects(
    authToken: string,
    courseId: string,
    type: 'members' | 'owners',
  ): Promise<CourseMember[]> {
    const graphApi = createGraphApi(authToken);
    let url: string | undefined = `/groups/${courseId}/${type}?$select=${COURSE_MEMBER_SELECT}&$top=${GRAPH_GROUP_MEMBERS_PAGE_SIZE}`;
    let records: CourseMember[] = [];

    while (url) {
      const response: AxiosResponse<ValueOfPaginated<CourseMember[]>> =
        await graphApi.get<ValueOfPaginated<CourseMember[]>>(url);
      records = [...records, ...response.data.value];
      // nextLink is an absolute URL, so axios ignores the baseURL for it
      url = response.data['@odata.nextLink'];
    }

    return records;
  }

  static async getCourseStudents(
    authToken: string,
    courseId: string,
  ): Promise<CourseMember[]> {
    const members = await this.getCourseMembers(authToken, courseId);
    return members.filter(m => m.userRole === UserRole.student);
  }
}
export { CourseService };
